import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  highestSequenceOf,
  usePreviewRunEventsQuery,
  usePreviewRunQuery,
} from "@/api/previewInsights";
import { usePreviewManualSessionQuery } from "@/api/previewManual";
import {
  SIM_POLL_INTERVAL_MS,
  isSimRunTerminal,
  usePreviewSimRunQuery,
} from "@/api/previewSim";
import { isTerminalQueryError } from "@/api/queryClient";
import { useCapability } from "@/auth/SessionContext";
import {
  EMPTY_LAB_TURNS,
  buildLabTurns,
  labProgress,
  type LabRunData,
} from "@/lib/labModel";
import {
  RUN_POLL_INTERVAL_MS,
  canStillChange,
  derivePreviewRunView,
} from "@/lib/previewInsights";

/**
 * Everything the Lab panes read about ONE run: envelope, sim status and the
 * compact event page, polled on the run page's own cadences. Never reads a
 * body: conversation text stays behind the explicit "Show conversation text".
 */
export function useLabRunData(
  projectId: string,
  previewRunRef: string | null,
): LabRunData | null {
  const ref = previewRunRef ?? "";
  const inspect = useCapability("preview.inspect");
  const simQuery = usePreviewSimRunQuery(projectId, ref, {
    enabled: inspect.allowed,
    pollIntervalMs: SIM_POLL_INTERVAL_MS,
  });
  const manualQuery = usePreviewManualSessionQuery(projectId, ref, {
    enabled: inspect.allowed,
  });
  // A sim run that has ended produces nothing more, but an envelope that is
  // "incomplete" is polled forever by contract (it cannot tell a lost range
  // from a run still going). The first envelope read made AFTER the sim ended
  // is therefore the last one worth polling for: from then on the run and its
  // events are read only on an explicit retry.
  const [settledRef, setSettledRef] = useState<string | null>(null);
  const run = usePreviewRunQuery(projectId, ref, {
    enabled: inspect.allowed,
    paused: settledRef === ref,
    pollIntervalMs: RUN_POLL_INTERVAL_MS,
  });
  if (
    settledRef !== ref &&
    simQuery.data != null &&
    isSimRunTerminal(simQuery.data) &&
    run.dataUpdatedAt > simQuery.dataUpdatedAt
  ) {
    setSettledRef(ref);
  }
  const settled = settledRef === ref;
  const view = useMemo(
    () => (run.data ? derivePreviewRunView(run.data) : null),
    [run.data],
  );
  const readable =
    view !== null &&
    (view.kind === "live" ||
      view.kind === "completed" ||
      view.kind === "incomplete");
  const live = view !== null && canStillChange(view) && !settled;
  const events = usePreviewRunEventsQuery(projectId, ref, {
    enabled: inspect.allowed && readable,
    pollIntervalMs: live ? RUN_POLL_INTERVAL_MS : false,
  });

  // CATCH UP TO THE ENVELOPE (same reasoning as PreviewRunConversation): the
  // envelope can observe the run's end and stop the event timer before the tick
  // that would have read the final turns, so one more read follows when the page
  // holds less than the envelope says the run reached.
  const envelopeLast = view?.summary?.lastSequence ?? null;
  const loadedLast = events.data?.content
    ? (highestSequenceOf(events.data.content.events) ?? 0)
    : null;
  const { refetch: refetchEvents, isFetching, isError } = events;
  const envelopeSeenIdle = useRef<number | null>(envelopeLast);
  const envelopeAtStart = useRef<number | null>(null);
  const wasFetching = useRef(false);
  useEffect(() => {
    if (isFetching) {
      if (!wasFetching.current) {
        envelopeAtStart.current = envelopeSeenIdle.current;
      }
    } else {
      envelopeSeenIdle.current = envelopeLast;
    }
    wasFetching.current = isFetching;
  }, [isFetching, envelopeLast]);
  useEffect(() => {
    if (!readable || isFetching || isError) {
      return;
    }
    if (
      loadedLast === null ||
      envelopeLast === null ||
      loadedLast >= envelopeLast
    ) {
      return;
    }
    const known = envelopeAtStart.current;
    if (known !== null && known >= envelopeLast) {
      return;
    }
    void refetchEvents();
  }, [readable, isFetching, isError, loadedLast, envelopeLast, refetchEvents]);

  const { refetch: refetchRun } = run;
  const { refetch: refetchSim } = simQuery;
  const { refetch: refetchManual } = manualQuery;
  const refetch = useCallback(() => {
    void refetchRun();
    void refetchSim();
    void refetchManual();
    if (readable) {
      void refetchEvents();
    }
  }, [refetchRun, refetchSim, refetchManual, refetchEvents, readable]);

  const content = events.data?.content ?? null;
  const lab = useMemo(
    () => (content ? buildLabTurns(content.events) : EMPTY_LAB_TURNS),
    [content],
  );
  const sim = simQuery.data ?? null;
  const manual = manualQuery.data ?? null;

  const terminal =
    (run.isError && isTerminalQueryError(run.error)) ||
    (events.isError && isTerminalQueryError(events.error));
  const contentState: LabRunData["contentState"] =
    previewRunRef === null || previewRunRef === ""
      ? "pending"
      : terminal
        ? "denied"
        : run.isPending
          ? "pending"
          : !readable
            ? run.isError && !run.data
              ? "error"
              : "unavailable"
            : events.isPending
              ? "pending"
              : events.isError && !events.data
                ? "error"
                : content === null
                  ? "unavailable"
                  : "ready";

  return useMemo(
    () =>
      previewRunRef === null || previewRunRef === ""
        ? null
        : {
            previewRunRef,
            view,
            sim,
            manual,
            candidateRevision:
              run.data?.dispatch.candidateRevision?.number ?? null,
            lab,
            records: content?.events ?? [],
            contentState,
            complete: content?.complete ?? true,
            live,
            progress: sim ? labProgress(sim) : null,
            stale:
              !terminal &&
              ((run.isError && run.data !== undefined) ||
                (events.isError && events.data !== undefined)),
            error: run.error ?? events.error ?? null,
            refetch,
          },
    [
      previewRunRef,
      view,
      sim,
      manual,
      lab,
      contentState,
      content,
      live,
      terminal,
      run.isError,
      run.data,
      run.error,
      events.isError,
      events.data,
      events.error,
      refetch,
    ],
  );
}
